import React, { useCallback, useState } from 'react';

import { Keyboard, Platform } from 'react-native';

import { useSelector } from 'react-redux';

import { StackNavigationProp } from '@react-navigation/stack';

import { getCreatePinUrl } from 'config/api.ts';
import { LANGUAGE_KEYS } from 'config/languageKeys.ts';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectAppState } from 'store/config/selectors';
import { PIN_TYPES } from 'store/user/reducer.types.ts';

import { RetryController } from 'services/opf-retry-mechanism';
import { isErrorNeedsRetry } from 'services/opf-retry-mechanism/utils.ts';

import NagraInstance from 'utils/axios/nagra/DefaultInstance.ts';

import PinDialog, { PinDialogRef } from 'components/PinDialog';
import { PinDialogStatus } from 'components/PinDialog/hooks/getPinDialogText';

type Props = {
    navigation: StackNavigationProp<
        RouteNavigationParams,
        SCREEN.PIN_SETUP_CREATE
    >;
};

export default function PinSetupIntroduction({ navigation }: Props) {
    const pinDialogRef = React.useRef<PinDialogRef>(null);
    const [pinDialogStatus, setPinDialogStatus] = useState<PinDialogStatus>(
        PinDialogStatus.FTI_CREATE_PIN,
    );
    const [pin, setPin] = useState('');

    const appState = useSelector(selectAppState);

    const onClose = () => {
        if (Platform.OS !== 'web') {
            Keyboard.dismiss();
        }

        navigation.pop();
    };

    const handleCreatePin = useCallback(
        async (_pin: string) => {
            const createPin = async () => {
                await NagraInstance.post(
                    getCreatePinUrl(),
                    {
                        type: PIN_TYPES.AccessPin,
                        value: _pin,
                    },
                    {
                        headers: { 'nv-tenant-id': 'nagra' },
                    },
                );

                navigation.replace(SCREEN.PIN_SETUP_SUCCESS);
            };

            try {
                await createPin();
                navigation.replace(SCREEN.PIN_SETUP_SUCCESS);
            } catch (error: any) {
                if (isErrorNeedsRetry(error)) {
                    RetryController.showVitalDialog({
                        actionButtonTitle: LANGUAGE_KEYS.CLOSE_BUTTON,
                        actionButtonOnPress: () => {
                            navigation.replace(SCREEN.PIN_SETUP_UNSUCCESS);
                            RetryController.hideVitalDialog();
                        },
                    });
                    RetryController.registerRetryCallback(createPin, {
                        retryDuration: 10 * 60 * 1000,
                        maxRetryCount: 10,
                    });
                    RetryController.executeRetry();
                } else {
                    navigation.replace(SCREEN.PIN_SETUP_UNSUCCESS);
                }
            }
        },
        [navigation],
    );

    const onSubmit = useCallback(
        (newPin: string) => {
            if (pinDialogStatus === PinDialogStatus.FTI_CREATE_PIN) {
                setPin(newPin);
                pinDialogRef.current?.clear();
                setPinDialogStatus(PinDialogStatus.FTI_CONFIRM_CREATE_PIN);
            } else if (
                (pinDialogStatus === PinDialogStatus.FTI_CONFIRM_CREATE_PIN ||
                    pinDialogStatus === PinDialogStatus.NEW_PIN_DOESNT_MATCH) &&
                pin
            ) {
                if (pin === newPin) {
                    handleCreatePin(pin);
                } else {
                    setPinDialogStatus(PinDialogStatus.NEW_PIN_DOESNT_MATCH);
                    pinDialogRef.current?.clear();
                }
            }
        },
        [handleCreatePin, pin, pinDialogStatus],
    );

    return (
        <PinDialog
            visible
            appState={appState}
            modal={false}
            ref={pinDialogRef}
            status={pinDialogStatus}
            haveToShowCloseButton={false}
            onSubmit={onSubmit}
            onClose={onClose}
        />
    );
}
